/**
 * SiteFooter — iter127 redesign.
 *
 * Walled-garden compliant: zero external links (no Twitter / Discord / GitHub
 * out-bound). Every "social" surface is on-platform — communities, leaderboard,
 * encrypted chat. The only `<a target="_blank">` allowed is the status page
 * which we host ourselves.
 *
 * Two variants:
 *   • "authed"  → minimal one-line strip (dashboard view)
 *   • "landing" / "public" → full 4-column grid with brand block, live trust
 *     signal ("online now: N"), and final brand stripe.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pulse } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { openCookieSettings } from "../components/CookieConsent";
import { FrictionFeedback } from "../components/FrictionFeedback";

const YEAR = new Date().getFullYear();
const LEGAL_NAME = "HACKASTRA INFOSEC L.L.C-FZ";

/**
 * Live "online now" pill — calls /api/stats/online-now (lightweight count
 * endpoint) once on mount. Silently degrades if the endpoint 404s. Used as
 * a walled-garden trust signal in the footer.
 */
function OnlineNowPill({ className = "", inline = false }) {
    const [n, setN] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.get("/stats/online-now")
            .then(({ data }) => { if (!cancelled) setN(data?.count); })
            .catch(() => { /* endpoint optional — silently degrade */ });
        return () => { cancelled = true; };
    }, []);
    if (n == null) return null;
    // Inline variant — plain text that inherits the surrounding bar's size/spacing
    // so it sits cleanly on the same line as "all systems operational".
    if (inline) {
        return (
            <>
                <span className="text-slate-800">·</span>
                <span data-testid="footer-online-now" className="inline-flex items-center gap-1 text-emerald-400 whitespace-nowrap">
                    online now: {n.toLocaleString()}
                </span>
            </>
        );
    }
    return (
        <span data-testid="footer-online-now" className={`inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-emerald-300 border border-emerald-500/40 bg-emerald-500/5 px-2 py-1 rounded-sm whitespace-nowrap ${className}`}>
            <Pulse size={11} weight="duotone" className="animate-pulse" />
            online now: {n.toLocaleString()}
        </span>
    );
}

export default function SiteFooter({ variant = "authed" }) {
    const testid =
        variant === "landing" ? "landing-footer"
            : variant === "public" ? "public-shell-footer"
                : "authed-home-footer";

    // Authed dashboard → no footer on any device (user request).
    if (variant === "authed") return null;

    // Public + landing variants → creative terminal-index footer.
    const GROUPS = [
        ["learn", [["/learn", "academy"], ["/tracks", "career tracks"], ["/certifications", "certifications"], ["/explorer", "explorer"]]],
        ["practice", [["/labs", "vulnerable labs"], ["/ctf", "ctf arena"], ["/network-labs", "network labs"], ["/investigations", "investigations"], ["/box", "attack box"], ["/tools", "payload tools"]]],
        ["compete", [["/multiplayer", "multiplayer"], ["/tournaments", "tournaments"], ["/leaderboard", "leaderboard"], ["/events", "competitions"], ["/teams", "teams"]]],
        ["hunt", [["/programs", "bounty directory"], ["/jobs", "security jobs"]]],
        ["community", [["/communities", "communities"], ["/q", "q&a"], ["/news", "news"], ["/faq", "faq"]]],
        ["company", [["/about", "about"], ["/pricing", "pricing"], ["/security", "security"], ["/changelog", "changelog"], ["/roadmap", "roadmap"], ["/press", "press kit"], ["/brand", "brand assets"], ["/contact", "contact"]]],
        ["legal", [["/legal/terms", "terms"], ["/legal/privacy", "privacy"], ["/legal/cookies", "cookies"], ["/legal/refunds", "refunds"], ["/legal/acceptable-use", "acceptable use"], ["/legal/copyright", "copyright"], ["/legal/community", "guidelines"], ["/legal/security", "report a vuln"], ["/sitemap", "sitemap"]]],
    ];
    return (
        <footer className="border-t border-white/[0.07] mt-16 bg-[#05070a] w-full" data-testid={testid}>
            {/* Statement block */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16 sm:pt-20 pb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">// the proving ground</div>
                    <div className="mt-4 text-[22px] min-[360px]:text-[26px] min-[420px]:text-3xl sm:text-4xl xl:text-5xl font-bold tracking-tighter text-slate-100 lowercase min-[360px]:whitespace-nowrap" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="footer-statement">
                        practice. compete. hunt<span className="text-slate-600 animate-pulse">_</span>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5 sm:gap-4 w-full md:w-auto shrink-0">
                    <Link to="/login?mode=register&type=hacker" data-testid="footer-cta-join"
                          className="w-full sm:w-auto bg-cyan-400 text-black text-[11px] uppercase tracking-[0.2em] font-mono font-bold px-4 sm:px-7 py-3 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2 whitespace-nowrap">
                        join the underground →
                    </Link>
                </div>
            </div>

            {/* Terminal index rows */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <div className="border-t border-white/[0.07]">
                    {GROUPS.map(([title, links]) => (
                        <div key={title} data-testid={`footer-col-${title}`}
                             className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 sm:gap-6 py-4 border-b border-white/[0.05] items-baseline">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">// {title}</span>
                            {/* Phone: aligned 2-col grid (no ragged wrapping). Desktop: inline row. */}
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:flex sm:flex-wrap sm:gap-x-5">
                                {links.map(([to, label]) => (
                                    <Link key={`${to}-${label}`} to={to}
                                          data-testid={`footer-link-${to.replace(/[/]/g, "-").slice(1) || "home"}`}
                                          className="font-mono text-xs lowercase text-slate-400 hover:text-slate-200 transition-colors whitespace-nowrap truncate">
                                        {label}
                                    </Link>
                                ))}
                                {title === "legal" && (
                                    <>
                                    <button onClick={openCookieSettings} data-testid="footer-cookie-settings"
                                            className="font-mono text-xs lowercase text-slate-400 hover:text-slate-200 transition-colors whitespace-nowrap truncate text-left">
                                        cookie settings
                                    </button>
                                    <FrictionFeedback />
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                    {/* Founder's ecosystem — the rest of the hacker toolkit (external). */}
                    <div data-testid="footer-col-toolkit"
                         className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 sm:gap-6 py-4 border-b border-white/[0.05] items-baseline">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">// toolkit</span>
                        <div className="grid grid-cols-1 gap-y-2 sm:flex sm:flex-wrap sm:gap-x-5">
                            <a href="https://whomi.bio" target="_blank" rel="noopener noreferrer" data-testid="footer-toolkit-whomi"
                               className="font-mono text-xs lowercase text-slate-400 hover:text-cyan-300 transition-colors whitespace-normal min-[400px]:whitespace-nowrap">
                                whomi.bio <span className="text-slate-500">— dev bio that proves you ship ↗</span>
                            </a>
                            <a href="https://crypt.pe" target="_blank" rel="noopener noreferrer" data-testid="footer-toolkit-cryptpe"
                               className="font-mono text-xs lowercase text-slate-400 hover:text-cyan-300 transition-colors whitespace-normal min-[400px]:whitespace-nowrap">
                                crypt.pe <span className="text-slate-500">— get paid in crypto · 0% platform fee ↗</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom bar — terminal prompt */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-xs tracking-normal text-slate-600 font-mono">
                    <span className="text-slate-600">$</span>
                    <span className="ml-1.5">echo &quot;© {YEAR} {LEGAL_NAME}<span className="hidden lg:inline"> · by hackers, for hackers</span>&quot;</span>
                </div>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 w-full md:w-auto justify-start md:justify-end text-[10px] uppercase tracking-[0.08em] sm:tracking-widest font-mono text-slate-600">
                    <span data-testid="footer-systems-ok" className="inline-flex items-center gap-1.5 text-emerald-400 whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> all systems operational
                    </span>
                    <OnlineNowPill inline />
                </div>
            </div>
        </footer>
    );
}
